import MeteomaticsTokenService from '@/services/meteomaticsToken.js';

// Constantes para parámetros comunes de Meteomatics
export const METEOMATICS_PARAMETERS = {
  // Precipitación
  PRECIPITATION_1H: 'precip_1h:mm',
  PRECIPITATION_24H: 'precip_24h:mm',
  PRECIPITATION_5MIN: 'precip_5min:mm',
  
  // Temperatura
  TEMPERATURE_2M: 't_2m:C',
  TEMPERATURE_MIN_2M: 't_min_2m_24h:C',
  TEMPERATURE_MAX_2M: 't_max_2m_24h:C',
  
  // Viento
  WIND_SPEED_10M: 'wind_speed_10m:ms',
  WIND_DIRECTION_10M: 'wind_dir_10m:d',
  WIND_GUSTS_10M: 'wind_gusts_10m_1h:ms',
  
  // Nubes y visibilidad
  CLOUD_COVER: 'total_cloud_cover:p',
  VISIBILITY: 'visibility:m',
  
  // Presión
  PRESSURE_MSL: 'msl_pressure:hPa',
  PRESSURE_SURFACE: 'pressure_100m:hPa'
};

export const METEOMATICS_SOURCES = {
  MIX: 'mix',
  ECMWF_IFS: 'ecmwf-ifs',
  GFS: 'gfs',
  ICON_EU: 'icon-eu',
  RADAR: 'radar'
};

export const METEOMATICS_CRS = {
  WGS84: 'CRS:84',
  WEB_MERCATOR: 'EPSG:3857'
};

/**
 * Utilidad para generar capas de tiles de Meteomatics
 */
export class MeteomaticsUtils {
  /**
   * Construye una URL de Meteomatics con autenticación OAuth
   * @param {string} baseUrl - URL base del endpoint de Meteomatics
   * @param {Object} parameters - Parámetros de la API de Meteomatics
   * @returns {Promise<string>} URL completa con token de acceso
   */
  static buildAuthenticatedUrlWithToken(baseUrl, token, parameters = {}) {
    const urlParams = new URLSearchParams({
      access_token: token,
      ...parameters
    });
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}${urlParams.toString()}`;
  }

  static async buildAuthenticatedUrl(baseUrl, parameters = {}) {
    const cached = MeteomaticsTokenService.getTokenSync();
    if (cached) {
      return this.buildAuthenticatedUrlWithToken(baseUrl, cached, parameters);
    }
    try {
      const token = await MeteomaticsTokenService.getValidToken();
      return this.buildAuthenticatedUrlWithToken(baseUrl, token, parameters);
    } catch (error) {
      console.error('Error construyendo URL autenticada de Meteomatics:', error);
      throw error;
    }
  }

  /**
   * Obtiene el token de Meteomatics del localStorage si está disponible
   * @returns {string|null} Token de acceso o null si no está disponible
   */
  static getStoredToken() {
    return MeteomaticsTokenService.getStoredToken();
  }

  /**
   * Verifica si hay un token válido de Meteomatics
   * @returns {boolean} True si hay un token válido
   */
  static hasValidToken() {
    return MeteomaticsTokenService.isTokenValid();
  }

  /**
   * Genera una URL para tiles WMTS de Meteomatics
   * @param {Object} options - Opciones para la URL de tiles WMTS
   * @param {string} options.source - Fuente de datos (ej: 'mix', 'ecmwf-ifs', 'gfs')
   * @param {string} options.parameter - Parámetro meteorológico (ej: 't_2m:C', 'precip_1h:mm', 'precip_5min:mm')
   * @param {string} options.crs - Sistema de coordenadas ('CRS:84' o 'EPSG:3857')
   * @param {number} options.z - Nivel de zoom (máximo 11)
   * @param {number} options.x - Coordenada X del tile
   * @param {number} options.y - Coordenada Y del tile
   * @param {string} options.validdate - Fecha y hora en formato ISO (YYYY-MM-DDThh:mm:ssZ)
   * @param {string} options.resolution - Resolución (256 o 512)
   * @param {string} options.format - Formato de imagen ('png' o 'webp')
   * @param {string} options.style - Estilo/colormap opcional
   * @returns {Promise<string>} URL del tile WMTS con autenticación
   */
  static buildWMTSTileUrlWithToken(token, {
    source = 'mix',
    parameter = 't_2m:C',
    crs = 'EPSG:3857',
    z,
    x,
    y,
    validdate,
    resolution = '512',
    format = 'webp',
    style = null
  }) {
    const baseUrl = `https://api.meteomatics.swiss/tiles/${source}/${parameter}/${crs}/${z}/${x}/${y}/${validdate}/${resolution}.${format}`;
    const params = new URLSearchParams({ access_token: token });
    if (style) {
      params.append('style', style);
    }
    return `${baseUrl}?${params.toString()}`;
  }

  static async buildWMTSTileUrl(options) {
    const cached = MeteomaticsTokenService.getTokenSync();
    if (cached) {
      return this.buildWMTSTileUrlWithToken(cached, options);
    }
    try {
      const token = await MeteomaticsTokenService.getValidToken();
      return this.buildWMTSTileUrlWithToken(token, options);
    } catch (error) {
      console.error('Error construyendo URL WMTS de Meteomatics:', error);
      throw error;
    }
  }

  /**
   * Genera una URL para tiles de precipitación
   * @param {Object} options - Opciones para precipitación
   * @param {number} options.z - Nivel de zoom
   * @param {number} options.x - Coordenada X del tile
   * @param {number} options.y - Coordenada Y del tile
   * @param {string} options.validdate - Fecha y hora en formato ISO
   * @param {string} options.source - Fuente de radar (por defecto 'mix')
   * @param {string} options.crs - Sistema de coordenadas (por defecto 'EPSG:3857')
   * @param {string} options.style - Estilo del radar (opcional)
   * @returns {Promise<string>} URL del tile de radar con autenticación
   */
  static async buildRadarTileUrl({
    z,
    x,
    y,
    validdate,
    source = 'mix',
    crs = 'EPSG:3857',
    style = null
  }) {
    return this.buildWMTSTileUrl({
      source,
      parameter: 'precip_1h:mm',
      crs,
      z,
      x,
      y,
      validdate,
      resolution: '512',
      format: 'webp',
      style
    });
  }

  /**
   * Genera una URL para datos puntuales de Meteomatics
   * @param {Object} options - Opciones para la consulta de datos
   * @param {string} options.datetime - Fecha y hora en formato ISO
   * @param {string} options.parameters - Parámetros separados por comas
   * @param {string} options.coordinates - Coordenadas en formato lat,lon
   * @param {string} options.format - Formato de respuesta (ej: 'json', 'csv')
   * @returns {Promise<string>} URL para consulta de datos con autenticación
   */
  static async buildDataUrl({
    datetime,
    parameters,
    coordinates,
    format = 'json'
  }) {
    const baseUrl = `https://api.meteomatics.com/${datetime}/${parameters}/${coordinates}/${format}`;
    return this.buildAuthenticatedUrl(baseUrl);
  }
}

export default MeteomaticsUtils;